import { useReducer, useCallback, useRef } from "react";

export type MyStateMap = Record<string, any>;

export type MyState<S extends MyStateMap> = {
  [K in keyof S]: S[K];
};

export type MyAction<S extends MyStateMap> = {
  type: keyof S & string;
  payload: S[keyof S & string];
};

export type MyDispatch<S extends MyStateMap> = <K extends keyof S & string>(
  type: K,
  payload: S[K]
) => void;

export function useMyState<S extends MyStateMap>(statePayloadMap: S) {
  type StateKey = keyof S & string;
  type State = MyState<S>;
  

  const initialState = Object.keys(statePayloadMap).reduce((acc, key) => {
    const typedKey = key as StateKey;
    acc[typedKey] = statePayloadMap[typedKey];
    return acc;
  }, {} as State);

  const reducer = (state: State, action: MyAction<S>): State => {
    return {
      ...state,
      [action.type]: action.payload,
    };
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const stateRef = useRef(state);
  stateRef.current = state;

  const setState = useCallback<MyDispatch<S>>(
    (type, payload) => {
      const currentValue = stateRef.current[type];
      if (currentValue !== payload) {
        dispatch({ type, payload });
      }
    },
    []
  );

  return [state, setState] as const;
}
